import { memo } from 'react';
import { Star } from 'lucide-react';
import type { TicketDetailData } from '@/entities/ticket';
import { cn } from '@/shared/lib/cn';
import styles from './TicketDetail.module.css';

const RATING_WORDS: Record<number, string> = {
  1: 'Плохо',
  2: 'Ниже ожиданий',
  3: 'Нормально',
  4: 'Хорошо',
  5: 'Отлично',
};

function ratingTone(rating: number): 'Done' | 'Neutral' | 'Danger' {
  if (rating >= 4) return 'Done';
  if (rating === 3) return 'Neutral';
  return 'Danger';
}

export const SatisfactionLine: React.FC<{
  rating: number | null;
  comment: string | null;
  plain?: boolean;
}> = ({ rating, comment, plain = false }) => (
  <div className={cn(styles.satisfactionLine, !plain && styles.satisfactionPlate)}>
    {rating != null && (
      <div className={styles.satisfactionHead}>
        <span
          className={styles.satisfactionStars}
          role="img"
          aria-label={`Оценка заявителя: ${rating} из 5 — ${RATING_WORDS[rating]}`}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={16}
              aria-hidden
              className={n <= rating ? styles.satisfactionStarOn : styles.satisfactionStarOff}
            />
          ))}
        </span>
        <strong className={styles.satisfactionWord} aria-hidden>
          {RATING_WORDS[rating]}
        </strong>
        <span className={styles.satisfactionCaption}>оценка заявителя</span>
      </div>
    )}
    {comment && <p className={styles.satisfactionComment}>«{comment}»</p>}
  </div>
);

const SatisfactionCalloutBase: React.FC<{ ticket: TicketDetailData }> = ({ ticket }) => {
  const rating = ticket.satisfactionRating;
  if (rating == null || rating < 1 || rating > 5) return null;
  return (
    <div className={cn(styles.resolutionCallout, styles[`satisfaction${ratingTone(rating)}`])}>
      <SatisfactionLine rating={rating} comment={ticket.satisfactionComment ?? null} plain />
    </div>
  );
};

export const SatisfactionCallout = memo(SatisfactionCalloutBase);
